import { useRef, useEffect } from 'react';

interface UseDropdownOptions {
  isOpen: boolean;
  onClose: () => void;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
}

/**
 * Reusable hook for managing dropdown/modal behavior
 * Handles click-outside detection and escape key closing with proper cleanup
 *
 * @param options.isOpen - Whether the dropdown is currently open
 * @param options.onClose - Callback to close the dropdown (should be stable, use useCallback if needed)
 * @param options.closeOnClickOutside - Whether to close on outside clicks (default: true)
 * @param options.closeOnEscape - Whether to close on Escape key (default: true)
 * @returns Ref to attach to the dropdown container element
 */
export function useDropdown({
  isOpen,
  onClose,
  closeOnClickOutside = true,
  closeOnEscape = true,
}: UseDropdownOptions) {
  const ref = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    if (!isOpen || !closeOnClickOutside) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (ref.current && target instanceof Node && !ref.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnClickOutside, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  return ref;
}
